/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Card, message, Spin, Typography } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import Header from '../Header';
import Sidebar from '../Sidebar';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const AIAssistant = () => {
  const [egnInput, setEgnInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nzisLoading, setNzisLoading] = useState(false);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Bulgarian EGN validation
  const validateEGN = (egn) => {
    // Basic Bulgarian EGN validation (10 digits)
    const egnRegex = /^\d{10}$/;
    return egnRegex.test(egn);
  };

  const handleEGNSubmit = async () => {
    if (!egnInput.trim()) {
      message.error('Моля въведете ЕГН');
      return;
    }

    if (!validateEGN(egnInput.trim())) {
      message.error('Невалиден ЕГН. Трябва да съдържа 10 цифри.');
      return;
    }

    const egn = egnInput.trim();
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: `ЕГН: ${egn}`,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setNzisLoading(true);

    try {
      // Step 1: Validate EGN exists in NZIS
      const nzisLoadingMessage = {
        id: Date.now() + 1,
        type: 'system',
        content: 'Проверяване на ЕГН в НЗИС...',
        timestamp: new Date(),
        isLoading: true
      };
      setMessages(prev => [...prev, nzisLoadingMessage]);

      const { default: NZISApi } = await import('../../api/nzis.api.js');
      const validation = await NZISApi.validatePatientEGN(egn);
      
      if (!validation.exists) {
        setMessages(prev => prev.map(msg => 
          msg.id === nzisLoadingMessage.id 
            ? { ...msg, content: `ЕГН ${egn} не беше намерен в НЗИС системата.`, isLoading: false, type: 'error' }
            : msg
        ));
        setNzisLoading(false);
        setLoading(false);
        setEgnInput('');
        return;
      }

      // Step 2: Fetch patient data from NZIS
      setMessages(prev => prev.map(msg => 
        msg.id === nzisLoadingMessage.id 
          ? { ...msg, content: 'Зареждане на пациентски данни от НЗИС...', isLoading: true }
          : msg
      ));

      const nzisData = await NZISApi.getPatientData(egn);

      // Step 3: Parse XML data to objects
      const { default: NZISXMLParser } = await import('../../utils/xmlParser.js');
      const parsedData = NZISXMLParser.parseNZISData(nzisData);

      // Step 4: Create patient summary
      const { PatientSummary } = await import('../../models/PatientData.js');
      const patientSummary = new PatientSummary(
        parsedData.visits || [],
        parsedData.prescriptions || [],
        parsedData.immunizations || [],
        parsedData.hospitalizations || [],
        parsedData.labResults || []
      );
      
      const cleanPatientData = patientSummary.toJSON();

      setMessages(prev => prev.map(msg => 
        msg.id === nzisLoadingMessage.id 
          ? { ...msg, content: 'Данни от НЗИС са заредени успешно. Подготовка за AI анализ...', isLoading: false }
          : msg
      ));

      setNzisLoading(false);

      // Step 5: Prepare OpenAI analysis (STOP HERE)
      const aiLoadingMessage = {
        id: Date.now() + 2,
        type: 'system', 
        content: 'Подготовка за AI анализ...',
        timestamp: new Date(),
        isLoading: true
      };
      setMessages(prev => [...prev, aiLoadingMessage]);

      // Initialize OpenAI service (but don't call yet)
      const { default: OpenAIService } = await import('../../services/openai.service.js');
      const openAIService = new OpenAIService(null); // No API key yet
      
      const aiPreparation = await openAIService.generatePatientSummary(cleanPatientData);

      // Show preparation results
      const preparationSummary = `
🔍 Анализ на данните:
• Общо медицински данни: ${aiPreparation.patientDataSummary.totalDataPoints}
• Категории: ${aiPreparation.patientDataSummary.categories.join(', ')}
• Очаквани токени: ~${aiPreparation.estimatedTokens}
• Модел: ${aiPreparation.model}

⚠️ ${aiPreparation.nextStep}
      `;

      setMessages(prev => prev.map(msg => 
        msg.id === aiLoadingMessage.id 
          ? { 
              ...msg, 
              content: preparationSummary,
              isLoading: false,
              type: 'info'
            }
          : msg
      ));

    } catch (error) {
      console.error('❌ Error processing EGN:', error);
      message.error('Грешка при обработка на ЕГН: ' + error.message);
      setNzisLoading(false);
    }

    setLoading(false);
    setEgnInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEGNSubmit();
    }
  };

  return (
    <div>
      <Header />
      <Sidebar activeClassName="ai-assistant" />
      
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="/admin-dashboard">Табло</a>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">AI Асистент</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Chat Interface */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">
                    <RobotOutlined /> AI Асистент за пациентски данни
                  </h4>
                  <p className="card-text">
                    Въведете ЕГН на пациент за получаване на анализ от НЗИС данните
                  </p>
                </div>
                
                <div className="card-body">
                  {/* Chat Messages */}
                  <div className="chat-container" style={{ 
                    height: '400px', 
                    overflowY: 'auto', 
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    {messages.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        color: '#6c757d',
                        paddingTop: '50px' 
                      }}>
                        <RobotOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <h5>Добре дошли в AI Асистента</h5>
                        <p>Въведете ЕГН на пациент за започване на анализ</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div 
                          key={msg.id}
                          style={{
                            display: 'flex',
                            justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                            marginBottom: '16px'
                          }}
                        >
                          <div
                            style={{
                              maxWidth: '70%',
                              padding: '12px 16px',
                              borderRadius: '16px',
                              background: 
                                msg.type === 'user' ? '#1890ff' :
                                msg.type === 'error' ? '#ff4d4f' :
                                msg.type === 'info' ? '#52c41a' : '#ffffff',
                              color: 
                                msg.type === 'user' || msg.type === 'error' || msg.type === 'info' 
                                  ? '#ffffff' : '#000000',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              position: 'relative',
                              whiteSpace: 'pre-line'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                              {msg.type === 'user' ? (
                                <UserOutlined style={{ marginRight: '8px' }} />
                              ) : (
                                <RobotOutlined style={{ marginRight: '8px' }} />
                              )}
                              <Text style={{ 
                                fontSize: '12px', 
                                color: msg.type === 'user' ? 'rgba(255,255,255,0.8)' : '#666'
                              }}>
                                {msg.type === 'user' ? 'Вие' : 'AI Асистент'}
                              </Text>
                            </div>
                            
                            <div>
                              {msg.isLoading ? (
                                <span>
                                  <Spin size="small" style={{ marginRight: '8px' }} />
                                  {msg.content}
                                </span>
                              ) : (
                                msg.content
                              )}
                            </div>
                            
                            <Text style={{ 
                              fontSize: '10px',
                              color: msg.type === 'user' ? 'rgba(255,255,255,0.6)' : '#999',
                              display: 'block',
                              marginTop: '4px'
                            }}>
                              {msg.timestamp.toLocaleTimeString('bg-BG', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </Text>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* EGN Input */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Input
                      placeholder="ЕГН (10 цифри)"
                      value={egnInput}
                      onChange={(e) => setEgnInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      maxLength={10}
                      style={{ 
                        width: '200px',
                        height: '40px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontSize: '16px',
                        fontFamily: 'monospace'
                      }}
                      autoComplete="off"
                    />
                    <Button 
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleEGNSubmit}
                      loading={loading}
                      style={{ 
                        height: '40px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      disabled={!egnInput.trim()}
                    >
                      {loading ? 'Обработка...' : 'Изпрати'}
                    </Button>
                  </div>
                  
                  {nzisLoading && (
                    <div style={{ 
                      marginTop: '16px', 
                      textAlign: 'center',
                      color: '#1890ff'
                    }}>
                      <Spin size="small" style={{ marginRight: '8px' }} />
                      Заявяване на данни от НЗИС...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;